import { Dispatch, SetStateAction, useState } from "react";
import classNames from "classnames";
import { useWatch } from "react-hook-form";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import NotificationBanner from "../../../NotificationBanner";

import TextLink from "../../../TextLinks";
import { Heading, Text } from "../../../Type";
import styles from "./index.module.scss";
import Modal from "../../../Modal";
import UploadDocumentsModal from "../../../ADP/UploadDocumentsModal";
import { useSectionsValid } from "../../../../hooks/useSectionsValid";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";

const Summary = ({
  setSelectedSection,
  salesRepStatus,
  readOnly,
}: {
  setSelectedSection: Dispatch<SetStateAction<string>>;
  salesRepStatus: boolean;
  readOnly: boolean;
}) => {
  const { state: { user }, } = useStoreContext();
  const { clientFile } = useClientFileContext();

  const [isUploadModalOpen, setUploadModalOpen] = useState(false);

  const isSalesRep = user?.role.name === "Sales Rep";
  const isAdmin = user?.role.name === "Administrator";

  const sec1: Array<string> = [];
  const sec2: Array<string> = [];
  const sec2a: Array<string> = [];
  const sec2b: Array<string> = [];
  const sec2c: Array<string> = [];
  const sec2d: Array<string> = [];
  const sec3: Array<string> = [];
  const sec4: Array<string> = [];

  // Show 2a/2b/2c/2d
  const showSection2a = useWatch({ name: "info.section2a-show" });
  const showSection2b = useWatch({ name: "info.section2b-show" });
  const showSection2c = useWatch({ name: "info.section2c-show" });
  const showSection2d = useWatch({ name: "info.section2d-show" });

  // Show 4
  const showSection4 = (clientFile.status === "PendingSignature");

  // Red errors check for each section
  const sectionsValid = useSectionsValid(true);

  // #region useWatch Calls
  // 1
  const currCoverage = useWatch({ name: "section1.coverage" });
  const versionCode = useWatch({ name: "section1.version-code" });
  const isCEP = useWatch({ name: "section1.cep" }) === "y-cep";

  //2
  const previousEquipment = useWatch({ name: "section2.previous-equipment" });
  const devicesRequired = useWatch({ name: "section2.devices-required" });

  //2a
  const checkFirstAccess2a =
    useWatch({ name: "section2a.reason-for-application" }) === "first-access";
  const checkModificationsToPreviouslyFunded2a =
    useWatch({ name: "section2a.reason-for-application" }) ===
    "modifications-previous";
  const checkModificationsToNonADP2a =
    useWatch({ name: "section2a.reason-for-application" }) ===
    "modifications-non-adp";
  const additionalAdpFundedOptions = useWatch({
    name: "section2a.additional-adp-funded-options",
  });
  const baseDevice = useWatch({ name: "section2a.base-device" });
  const is1MoveThroughout =
    useWatch({ name: "section2a.move-throughout-residence" }) === "mt-yes";
  const is2MoveBeyond =
    useWatch({ name: "section2a.move-beyond-residence" }) === "mb-yes";
  const is3inaccessible =
    useWatch({ name: "section2a.move-through-inaccessible" }) === "in-yes";
  const is4independentlyMobile =
    useWatch({ name: "section2a.independently-mobile" }) === "im-yes";
  const is5requiresForearmCrutches =
    useWatch({ name: "section2a.requires-forearm-crutches" }) === "fc-yes";
  const is6specificStandingFrame =
    useWatch({ name: "section2a.specific-standing-frame" }) === "sf-yes";
  const brakesType = useWatch({ name: "section2a.brake-type" });
  const brakes = useWatch({ name: "section2a.brakes" });

  //2b
  const additionalAdpFundedOptions2b = useWatch({
    name: "section2b.additional-adp-funded-options",
  });
  const baseDevice2b = useWatch({ name: "section2b.base-device" });
  const checkFirstAccess2b =
    useWatch({ name: "section2b.reason-for-application" }) === "first-access";
  const checkModificationsToPreviouslyFunded2b =
    useWatch({ name: "section2b.reason-for-application" }) ===
    "modifications-previous";
  const checkModificationsToNonADP2b =
    useWatch({ name: "section2b.reason-for-application" }) ===
    "modifications-non-adp";
  const getPrescriptionDetails = useWatch({
    name: [
      "section2b.seat-width",
      "section2b.seat-depth",
      "section2b.seat-to-floor",
      "section2b.back-cane-height", // 6: leg-rest-length can be empty
      "section2b.client-weight",
    ],
  });

  const is1MoveThroughout2b =
    useWatch({
      name: "section2b.move-throughout-residence",
    }) || "";
  const is2MoveBeyond2b =
    useWatch({ name: "section2b.move-beyond-residence" }) || "";
  const is3ThroughoutDependent2b =
    useWatch({
      name: "section2b.move-throughout-dependent",
    }) || "";
  const is4BeyondDependent2b =
    useWatch({
      name: "section2b.move-beyond-dependent",
    }) || "";
  const is5ThroughoutIndependent2b =
    useWatch({
      name: "section2b.move-independently-throughout",
    }) || "";
  const is6MoveIndependently2b = useWatch({
    name: "section2b.move-independently-beyond",
  }) || "";
  const is7WeightShift2b =
    useWatch({
      name: "section2b.weight-shift-independently",
    }) || "";
  const is8HistoryTissue2b =
    useWatch({
      name: "section2b.history-tissue-trauma",
    }) || "";
  const is10IntoleranceSetting2b =
    useWatch({
      name: "section2b.intolerance-sitting",
    }) || "";
  const is11SomePowerThroughput2b =
    useWatch({
      name: "section2b.some-power-throughput",
    }) || "";
  const is12SomePowerBeyond2b =
    useWatch({
      name: "section2b.some-power-beyond",
    }) || "";
  const is13Anticipated2b = useWatch({ name: "section2b.anticipated" }) || "";
  const provideClinicalRationale2b =
    useWatch({
      name: "section2b.provide-clinical-rationale",
    }) || "";

    //2c
  const checkFirstAccess2c =
    useWatch({ name: "section2c.reason-for-application" }) === "first-access";
  const checkModificationsToPreviouslyFunded2c =
    useWatch({ name: "section2c.reason-for-application" }) ===
    "modifications-previous";
  const checkModificationsToNonADP2c =
    useWatch({ name: "section2c.reason-for-application" }) ===
    "modifications-non-adp";
  const is1MoveThroughoutPowerBase2c =
    useWatch({ name: "section2c.base-move-throughout-residence" }) ===
    "bmt-yes";
  const is2MoveBeyondPowerBase2c =
    useWatch({ name: "section2c.base-move-beyond-residence" }) === "bmb-yes";
  const is1MoveThroughoutScooter2c =
    useWatch({ name: "section2c.scooter-move-throughout-residence" }) ===
    "smt-yes";
  const is2MoveBeyondScooter2c =
    useWatch({ name: "section2c.scooter-move-beyond-residence" }) === "smb-yes";
  const is3ScooterSeatTiller =
    useWatch({ name: "section2c.scooter-seat-tiller" }) === "sst-yes";
  const specialityOptions = useWatch({ name: "section2c.speciality-options" });
  const powerPositioningDevice = useWatch({
    name: "section2c.power-positioning-devices",
  });
  const baseDeviceIsPowerScooter =
    useWatch({ name: "section2c.base-device" }) === "power-scooter";
  const baseDevice2c = useWatch({ name: "section2c.base-device" });
  const medicalCondition = useWatch({name: "section2c.medical-condition"});

  //2d
  const checkFirstAccess2d =
    useWatch({ name: "section2d.reason-for-application" }) === "first-access";
  const checkModificationsToPreviouslyFunded2d =
    useWatch({ name: "section2d.reason-for-application" }) ===
    "modifications-previous";
  const checkModificationsToNonADP2d =
    useWatch({ name: "section2d.reason-for-application" }) ===
    "modifications-non-adp";
  const isSeatingComponent =
    useWatch({ name: "section2d.seating-components" }) === "sc-yes";
  const isRequiresTray =
    useWatch({ name: "section2d.requires-tray" }) === "tray-yes";
  const isTray = useWatch({ name: "section2d.tray" });

  // #endregion

  //#region logic
  // SECTION 1
  if (currCoverage?.length > 0) {
    sec1.push(
      "ADP does not fund clients which have coverage under WSIB/VAC clients. Please check the ADP manual for further information and review your answers again."
    );
  }

  if (versionCode?.length < 1) {
    sec1.push(
      "Please provide a version code if there is one on the health card."
    );
  }

  // SECTION 2
  const checkAnyPreviouslyFunded = // This is for the "None" checkbox in Section 2, Previous Equipment.
    Array.isArray(previousEquipment) &&
    previousEquipment?.length > 0 &&
    !previousEquipment.includes("prev-none");

  const checkNonADP =
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-modifications-non-ADP");

  const checkPreviouslyADP =
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-modifications-ADP");

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-wheeled-walker") &&
    (devicesRequired?.includes("req-power-base") ||
      devicesRequired?.includes("req-manual-wheelchair-independent"))
  ) {
    // https://app.asana.com/0/0/1201078110443935/f (1)
    sec2.push(
      "If you have selected a 'wheeled walker only to achieve independent mobility', then you should not be selecting a 'power base only to achieve independent mobility' or 'manual wheelchair only to achieve independent mobility' separately. Please review your answers in Section 2, page 2 again to make the right selections. To get both these devices, please select any of the following options: \"An ambulation aid and a manual wheelchair to achieve independent mobility\" or \"An ambulation aid and a power base/scooter to achieve independent mobility\"."
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-forearm-crutches") &&
    (devicesRequired?.includes("req-power-base") ||
      devicesRequired?.includes("req-manual-wheelchair-independent"))
  ) {
    // https://app.asana.com/0/0/1201078110443935/f (1)
    sec2.push(
      "If you have selected 'forearm crutches only to achieve independent mobility', then you should not select 'power base only to achieve independent mobility' or 'manual wheelchair only to achieve independent mobility'. Please review your answers in Section 2, page 2 again to make the right selections. To get both these devices, please select any of the following options: \"An ambulation aid and a manual wheelchair to achieve independent mobility\" or \"An ambulation aid and a power base/scooter to achieve independent mobility\""
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-manual-wheelchair-independent") &&
    (devicesRequired?.includes("req-power-base") ||
      devicesRequired?.includes("req-power-scooter"))
  ) {
    // https://app.asana.com/0/0/1201078110443935/f (1) + GSheets "New Logic" Row 2
    sec2.push(
      "You have indicated that the client requires a manual wheelchair only as well as a power wheelchair only. This is inconsistent. Please go back and review the \"Devices currently required by the applicant\" section."
    );
  }

  // SECTION 2a
  if (checkAnyPreviouslyFunded && checkFirstAccess2a) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2a.push(
      'Selecting "First Access" in this section is inconsistent with information provided on Section 2, page 2, where you have indicated that the client has previously funded equipment through the ADP program. Please check your answers in these sections before proceeding.'
    );
  }

  if (checkPreviouslyADP && !checkModificationsToPreviouslyFunded2a) {
    // https://app.asana.com/0/0/1200354578179106/f (2)
    sec2a.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications/Adjustments/Additional Components to Previously ADP Funded Devices Currently in use".  This appears inconsistent with the reason for application selection in Section 2, page 2. Under the "Devices currently required by the applicant on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to previously ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (checkModificationsToPreviouslyFunded2a && !checkAnyPreviouslyFunded) {
    // https://app.asana.com/0/1200926940298631/1201105375293951/f
    sec2a.push(
      "'Modifications/adjustments/additional components to previously ADP funded device(s) currently in use' was selected in Section 2a, but 'None' was selected under previous equipment in Section 2."
    );
  }

  if (checkNonADP && !checkModificationsToNonADP2a) {
    // https://app.asana.com/0/0/1200354578179106/f (3)
    sec2a.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications to Non ADP Funded Device(s)".  This appears inconsistent with the reason for application selection in Section 2, page 2.  Under the  "Devices currently required by the applicnt on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to non ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (brakes === "brake-none" && brakesType !== "brakeType-none") {
    // https://app.asana.com/0/0/1201078110443955/f + GSheets Row 4
    sec2a.push(
      "Your prescription indicates no brakes, yet a brake type (bilateral or one hand) has been selected. This appears to be inconsistent. Please go back and review your responses."
    );
  }

  const lengthAdditionalAdpFundedOptions = Array.isArray(
    additionalAdpFundedOptions
  )
    ? additionalAdpFundedOptions.length
    : 0;
  if (
    Array.isArray(baseDevice) &&
    lengthAdditionalAdpFundedOptions > 1 &&
    baseDevice.some((i) =>
      ["pswwt1", "pswwt2", "pswwwf", "psft1", "psft2"].includes(i)
    )
  ) {
    //https://app.asana.com/0/0/1200379806606166/f
    sec2a.push(
      'If a paediatric device is selected in Section 2a, only one "Additional ADP Option" can be selected.'
    );
  }

  const aspsww = (additionalAdpFundedOptions ? additionalAdpFundedOptions.find(x => x === "adolescent-size-paediatric-specific") : undefined);
  const pswwt = (baseDevice ? baseDevice?.find(x => x === "pswwt1" || x === "pswwt2") : undefined);
  if( aspsww && !pswwt) {
    // https://app.asana.com/0/1201417885569419/1201992944879823
    sec2a.push(
      'You have selected "Adolescent Size Paediatric Specific Wheeled Walker", but have not selected a "Pediatric Specific Wheeled Walker" as a base device. Your answers in this section appear inconsistent, please go back and review these sections.'
    );
  }

  const aspw = (additionalAdpFundedOptions ? additionalAdpFundedOptions.find(x => x === "adolescent-size-paediatric-wheeled") : undefined);
  const pswwf = (baseDevice ? baseDevice?.find(x => x === "pswwwf") : undefined);
  if( aspw && !pswwf) {
    // https://app.asana.com/0/1201417885569419/1201992944879823
    sec2a.push(
      'You have selected "Adolescent Size Paediatric Wheeled Walker – Walking Frame", but have not selected a "Paediatric Specific Wheeled Walker Walking Frame" as a base device. Your answers in this section appear inconsistent, please go back and review these sections.'
    );
  }

  const asps = (additionalAdpFundedOptions ? additionalAdpFundedOptions.find(x => x === "adolescent-size-paediatric-standing") : undefined);
  const psft = (baseDevice ? baseDevice?.find(x => x === "psft1" || x === "psft2") : undefined);
  if( asps && !psft) {
    // https://app.asana.com/0/1201417885569419/1201992944879823
    sec2a.push(
      'You have selected "Adolescent Size Paediatric Standing Frame", but have not selected a "Paediatric Standing Frame" as a base device. Your answers in this section appear inconsistent, please go back and review these sections.'
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-forearm-crutches") &&
    !(
      (is1MoveThroughout || is2MoveBeyond || is3inaccessible) &&
      is4independentlyMobile &&
      is5requiresForearmCrutches
    )
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (1)
    sec2a.push(
      'You have prescribed an ambulation aid, yet the answers to the "Confirmation of Applicant\'s Eligibility for Ambulation Aids" does not appear to indicate that the client is using them for independent mobility and that they are a required device for this client.  Please go back and review these sections.'
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-wheeled-walker") &&
    !((is1MoveThroughout || is2MoveBeyond) && is4independentlyMobile)
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (2)
    sec2a.push(
      "This answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2.  For more information on the ADP eligibility criteria please see the ADP manual, Part 3 and Part 4 for more information about the eligibility criteria."
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-ambulation-aid-manual") &&
    !(
      (is1MoveThroughout || is2MoveBeyond || is3inaccessible) &&
      is4independentlyMobile
    )
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (3)
    sec2a.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2.  See the ADP manual, Part 3 and Part 4 for more information about ADP eligibility criteria"
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-ambulation-aid-power") &&
    !(
      (is1MoveThroughout || is2MoveBeyond || is3inaccessible) &&
      is4independentlyMobile
    )
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (4)
    sec2a.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual, Part 3 and Part 4 for more information about ADP eligibility criteria"
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-paediatric-standing") &&
    Array.isArray(baseDevice) &&
    baseDevice.some((i) => ["psft1", "psft2"].includes(i)) &&
    !is6specificStandingFrame
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (5)
    sec2a.push(
      'You have prescribed a pediatric standing frame in section 2a, but in the "Confirmation of Applicant\'s Eligibility for Ambulation Aids" section you have indicated that the client does not need a standing frame or have answered N/A. This appears to be inconsistent. Please go back and review your answers. See the ADP manual, Part 3 and Part 4 for more information about ADP eligibility criteria.'
    );
  }

  if (
    Array.isArray(baseDevice) &&
    baseDevice.some((i) => ["forearm"].includes(i)) &&
    !is5requiresForearmCrutches
  ) {
    // https://app.asana.com/0/1200279486914563/1200379806606163/f (6)
    // Update: https://app.asana.com/0/0/1201494546391830/f
    sec2a.push(
      "Inconsistency in the application: in question 5, you have indicated that the client does not require forearm crutches, but Forearm crutches are selected as a base device. Please go back and check your answers."
    );
  }

  //SECTION 2b
  if (checkAnyPreviouslyFunded && checkFirstAccess2b) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2b.push(
      'Selecting "First Access" in this section is inconsistent with information provided on Section 2, page 2, where you have indicated that the client has previously funded equipment through the ADP program. Please check your answers in these sections before proceeding.'
    );
  }

  if (checkPreviouslyADP && !checkModificationsToPreviouslyFunded2b) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2b.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications/Adjustments/Additional Components to Previously ADP Funded Devices Currently in use".  This appears inconsistent with the reason for application selection in Section 2, page 2. Under the "Devices currently required by the applicant on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to previously ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (checkModificationsToPreviouslyFunded2b && !checkAnyPreviouslyFunded) {
    // https://app.asana.com/0/1200926940298631/1201105375293951/f
    sec2b.push(
      "'Modifications/adjustments/additional components to previously ADP funded device(s) currently in use' was selected in Section 2b, but 'None' was selected under previous equipment in Section 2."
    );
  }

  if (checkNonADP && !checkModificationsToNonADP2b) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2b.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications to Non ADP Funded Device(s)".  This appears inconsistent with the reason for application selection in Section 2, page 2.  Under the  "Devices currently required by the applicnt on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to non ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (
    Array.isArray(additionalAdpFundedOptions2b) &&
    additionalAdpFundedOptions2b.includes("smwfwmdt") &&
    !(baseDevice2b === "amdtw" || baseDevice2b === "pmdtw")
  ) {
    // https://app.asana.com/0/0/1200379806606178/f (2)
    sec2b.push(
      'Under the "Additional ADP Funded Options" section you have prescribed a "Standard manual wheelchair frame with Manual dynamic device"  This does not appear to be consistent with the base device you selected.  Please go back and review this section. See the ADP manual, Part 3 and Part 4 for more information about ADP eligibility criteria.'
    );
  }

  if (
    Array.isArray(getPrescriptionDetails) &&
    (getPrescriptionDetails?.includes(undefined) ||
      getPrescriptionDetails?.includes(""))
  ) {
    // https://app.asana.com/0/0/1200354578179085/f
    sec2b.push(
      "All details should ideally be answered in prescription details. At least one mark per statement should be there, except statement 6"
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-manual-wheelchair-independent") &&
    !(
      is1MoveThroughout2b?.includes("yes") ||
      is3ThroughoutDependent2b?.includes("yes")
    )
  ) {
    // https://app.asana.com/0/1200279486914563/1200406570252318/f (1)
    sec2b.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual Part 3 and 4 for more information about ADP eligibility criteria."
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-manual-wheelchair-propulsion") &&
    !is3ThroughoutDependent2b.includes("mtd-yes")
  ) {
    // https://app.asana.com/0/1200279486914563/1201488836914642
    sec2b.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be dependent using the device, and then answers to the eligibility questions in this section indicate that the client is independent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual (Part 3 and Part 4) for more information about ADP eligibility criteria."
    );
  }


  // If manual dynamic tilt wheelchair is selected to achieve independent mobility,  options 1 or 2 must be yes.
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-dynamic-tilt-independent") &&
    !(
      is1MoveThroughout2b?.includes("mt-yes") ||
      is2MoveBeyond2b.includes("mb-yes")
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be indepdendent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using ths device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria."
    )
  }

  // If manual dynamic tilt wheelchair is selected to achieve independent mobility, and IF options 5 or 6 is yes, and titanium frame is not seleced on page 6
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-dynamic-tilt-independent") &&
    (
      is5ThroughoutIndependent2b.includes("mit-yes") ||
      is6MoveIndependently2b?.includes("mib-yes")
    ) && (
      Array.isArray(additionalAdpFundedOptions2b) &&
      !(additionalAdpFundedOptions2b.includes("titanium-frame"))
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "You have indicated that the client requires a titanium frame chair, and have selected a manual dynamic tilt wheelchair as the base devices. This appears to be inconsistent. Please review your responses"
    )
  }

  if (
    Array.isArray(additionalAdpFundedOptions2b) &&
    (additionalAdpFundedOptions2b.includes("titanium-frame")
    ) && (
      Array.isArray(devicesRequired) &&
      devicesRequired?.includes("req-dynamic-tilt-independent") &&
      !(
      is5ThroughoutIndependent2b.includes("mit-yes") ||
      is6MoveIndependently2b?.includes("mib-yes")
      )
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "You have indicated that the client requires a titanium frame chair, and have selected a manual dynamic tilt wheelchair as the base devices. This appears to be inconsistent. Please review your responses"
    )
  }

  //If a manual dynamic tilt wheelchair to achieve independent mobility, is selected, if 7 = no OR 8 =Yes OR 10 = yes an error message should NOT occur.
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-dynamic-tilt-independent") &&
    !(
      is7WeightShift2b.includes("wsi-no") ||
      is8HistoryTissue2b.includes("htt-yes") ||
      is10IntoleranceSetting2b.includes("is-yes")
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "You have prescribed a manual tilt wheelchair, however your answers to questions 7, 8,  and 10 appear to indicate that the client is not eligible for this device. Please review your responses. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria."
    )
  }

  //If manual dynamic tilt wheelchair to achieve mobility (dep on propulsion), is selected, 1 and 2 both cannot be yes, 3 or 4 must be yes.
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-dynamic-tilt-propulsion") &&
    (
      (is1MoveThroughout2b?.includes("mt-yes") &&
      is2MoveBeyond2b.includes("mb-yes")) ||
      !(
        is3ThroughoutDependent2b.includes("mtd-yes") ||
        is4BeyondDependent2b.includes("mbd-yes")
      )
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be dependent using the device, and then answers to the eligibility questions in this section indicate that the client is independent, or not using ths device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria."
    )
  }

  //If a manual dynamic tilt wheelchair to achieve mobility (dep on propulsion), is selected, if 7 = no OR 8 =Yes OR 10 = yes an error message should NOT occur
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-dynamic-tilt-propulsion") &&
    !(
      is7WeightShift2b.includes("wsi-no") ||
      is8HistoryTissue2b.includes("htt-yes") ||
      is10IntoleranceSetting2b.includes("is-yes")
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "You have prescribed a manual tilt wheelchair, however your answers to questions 7, 8 and 10 appear to indicate that the client is not elibile for this device. Please review your responses. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria."
    )
  }

  //If manual wheelchair with a power add on device to achieve independent mobility, is selected, 11 or 12 needs to be yes
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-manual-wheelchair-power") &&
    !(
      is11SomePowerThroughput2b.includes("spt-yes") ||
      is12SomePowerBeyond2b.includes("spb-yes")
    )
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      'You have prescribed a power add on device, however the answers to questions 11 and 12 under "Confirmation of Applicant\'s Eligibility for a Manual Wheelchair" do not appear to indicate that the client requires power.  Please review these sections. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria.'
    )
  }

  //  If a manual wheelchair with a power add-on device to achieve independent mobility is selected, then statement 13 (for eligibility) needs to be a yes.
  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-manual-wheelchair-power") &&
      !(is13Anticipated2b.includes("ant-yes"))
  ) {
    //https://app.asana.com/0/1200279486914563/1201367780677928/f
    sec2b.push(
      "You have prescribed a power add on device, but your answer to question 13 does not appear to be consistent with the ADP eligibility criteria for power add on devices. Please review this section. See the ADP manual (Part 3 and Part 4) for more informaton about ADP eligibility criteria."
    )
  }

  const smwfwmdt = (Array.isArray(additionalAdpFundedOptions2b) ? additionalAdpFundedOptions2b.find(x => x === "smwfwmdt" || x === "titanium-frame") : undefined);
  if(!smwfwmdt && provideClinicalRationale2b.length > 0) {
    // https://app.asana.com/0/1200279486914563/1202016566414311
    sec2b.push(
      "You have provided clinical rationale for either a 'Titanium Frame' or 'Standard Manual Wheelchair Frame with Manual Dynamic Tilt', but not selected either of these devices. Your answers in this section appear inconsistent, please go back and review these sections."
    )
  }

  //SECTION 2c
  if (checkAnyPreviouslyFunded && checkFirstAccess2c) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2c.push(
      'Selecting "First Access" in this section is inconsistent with information provided on Section 2, page 2, where you have indicated that the client has previously funded equipment through the ADP program. Please check your answers in these sections before proceeding.'
    );
  }

  if (checkPreviouslyADP && !checkModificationsToPreviouslyFunded2c) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2c.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications/Adjustments/Additional Components to Previously ADP Funded Devices Currently in use".  This appears inconsistent with the reason for application selection in Section 2, page 2. Under the "Devices currently required by the applicant on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to previously ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (checkModificationsToPreviouslyFunded2c && !checkAnyPreviouslyFunded) {
    // https://app.asana.com/0/1200926940298631/1201105375293951/f
    sec2c.push(
      "'Modifications/adjustments/additional components to previously ADP funded device(s) currently in use' was selected in Section 2c, but 'None' was selected under previous equipment in Section 2."
    );
  }

  if (checkNonADP && !checkModificationsToNonADP2c) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2c.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications to Non ADP Funded Device(s)".  This appears inconsistent with the reason for application selection in Section 2, page 2.  Under the  "Devices currently required by the applicnt on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to non ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-power-base") &&
    !(is1MoveThroughoutPowerBase2c || is2MoveBeyondPowerBase2c)
  ) {
    // https://app.asana.com/0/1200279486914563/1200406570252321/f (1)
    sec2c.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual for more information about ADP eligibility criteria."
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-power-scooter") &&
    !(
      (is1MoveThroughoutScooter2c || is2MoveBeyondScooter2c) &&
      is3ScooterSeatTiller
    )
  ) {
    // https://app.asana.com/0/1200279486914563/1200406570252321/f (2)
    sec2c.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual for more information about ADP eligibility criteria."
    );
  }

  if (
    (is1MoveThroughoutPowerBase2c || is2MoveBeyondPowerBase2c) &&
    (is1MoveThroughoutScooter2c || is2MoveBeyondScooter2c)
  ) {
    // https://app.asana.com/0/0/1201147469640683/f ([1]/[2])
    sec2c.push(
      "If 1 or 2 is answered yes for Power Scooter, 1 or 2 should not be yes for Power Base, and vice versa."
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-ambulation-aid-power") &&
    !(is1MoveThroughoutPowerBase2c || is2MoveBeyondPowerBase2c)
  ) {
    // https://app.asana.com/0/1200279486914563/1200406570252321/f (3)
    sec2c.push(
      "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using this device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual for more information about ADP eligibility criteria."
    );
  }

  if (
    baseDeviceIsPowerScooter &&
    ((specialityOptions && specialityOptions.length !== 0) ||
      (powerPositioningDevice && powerPositioningDevice.length !== 0))
  ) {
    sec2c.push(
      "You have selected power scooter as the base device in Section 2c. Typically, you should not be choosing any power positioning device or speciality controls in this section. Please check your answers in this section before proceeding. "
    );
  }

  if (
    (baseDevice2c === "apbt1" || baseDevice2c === "ppbt1") &&
    (specialityOptions.length !== 0 ||
      powerPositioningDevice.includes("ppd-1") ||
      powerPositioningDevice.includes("ppd-3"))
  ) {
    sec2c.push(
      "You have selected a Power Base Type 1 as the base device in Section 2c. Typically, you should not be selecting a power tilt positioning device or any of the specialty controls in this section. Please check your answers in this section before proceeding. "
    );
  }

  const specialityCount = (Array.isArray(specialityOptions) ? specialityOptions.length : 0);
  if(specialityCount === 0 && medicalCondition.length > 0) {
    // https://app.asana.com/0/1200279486914563/1202016566414311
    sec2c.push(
      "You have provided clinical rationale for a specialty component, but not selected which component is needed. Your answers in this section appear inconsistent, please go back and review these sections."
    )
  }

  //SECTION 2d
  if (checkAnyPreviouslyFunded && checkFirstAccess2d) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2d.push(
      'Selecting "First Access" in this section is inconsistent with information provided on Section 2, page 2, where you have indicated that the client has previously funded equipment through the ADP program. Please check your answers in these sections before proceeding.'
    );
  }

  if (checkPreviouslyADP && !checkModificationsToPreviouslyFunded2d) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2d.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications/Adjustments/Additional Components to Previously ADP Funded Devices Currently in use".  This appears inconsistent with the reason for application selection in Section 2, page 2. Under the "Devices currently required by the applicant on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to previously ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (checkModificationsToPreviouslyFunded2d && !checkAnyPreviouslyFunded) {
    // https://app.asana.com/0/1200926940298631/1201105375293951/f
    sec2d.push(
      "'Modifications/adjustments/additional components to previously ADP funded device(s) currently in use' was selected in Section 2d, but 'None' was selected under previous equipment in Section 2."
    );
  }

  if (checkNonADP && !checkModificationsToNonADP2d) {
    // https://app.asana.com/0/0/1200354578179106/f
    sec2d.push(
      'Your responses may be inconsistent: In the "Reason for application" section you have not chosen: "Modifications to Non ADP Funded Device(s)".  This appears inconsistent with the reason for application selection in Section 2, page 2.  Under the  "Devices currently required by the applicnt on an ongoing daily basis, Based on Eligibility Criteria for ADP funding Assistance" you had selected "Modifications to non ADP funded device(s)"  Please go back and review these sections.'
    );
  }

  if (
    Array.isArray(devicesRequired) &&
    devicesRequired?.includes("req-positioning-devices") &&
    !(isSeatingComponent || isRequiresTray)
  ) {
    // https://app.asana.com/0/0/1200406570252330/f (1)
    // Update: https://app.asana.com/0/1200279486914563/1201488836914646/f
    sec2d.push(
      "Since positioning device for a wheelchair is selected in Section 2, the answer to the eligibility questions would appear to indicate that the client is not eligible for funding."
    );
  }

  // commented out because we need to delete this error copy - https://app.asana.com/0/1200279486914563/1201488837106369
  // const anyPositioningDeviceSelected = allPositioningDevices.some(
  //   (i) => Array.isArray(i) && i?.length > 0
  // );

  // if (anyPositioningDeviceSelected && !isSeatingComponent) {
  //   // https://app.asana.com/0/0/1200406570252330/f (2)
  //   // Update: https://app.asana.com/0/0/1201488837106369/f
  //   sec2d.push(
  //     "The answers in this section appear to be inconsistent with the answers provided on page 2. Typically this error message appears when an option on page 2 indicates the client will be independent using the device, and then answers to the eligibility questions in this section indicate that the client is dependent, or not using ths device for mobility. Please go back and review this section to make sure the responses are consistent with answers provided on page 2. See the ADP manual for more informaton about ADP eligibility criteria."
  //   );
  // }

  if (Array.isArray(isTray) && isTray.length > 0 && !isRequiresTray) {
    // https://app.asana.com/0/0/1200406570252330/f (2)
    sec2d.push(
      "Answers are inconsistent with information provided on Section 2, page 9, where you have indicated that the client needs a tray, but have indicated that the client does not require a tray in the eligibility questions. Please check your answer again before proceeding. See the ADP manual for more information about ADP eligibility criteria."
    );
  }

  if (!(Array.isArray(isTray) && isTray.length > 0) && isRequiresTray) {
    // https://app.asana.com/0/0/1200406570252330/f (2)
    sec2d.push(
      "Answers are inconsistent with information provided on Section 2, page 9, where you have indicated that the client does not need a tray, but have indicated that the client does require a tray in the eligibility questions. Please check your answer again before proceeding. See the ADP manual for more information about ADP eligibility criteria."
    );
  }

  //#endregion

  function renderSectionError(hasError: boolean) {
    if (!hasError) return null;
    return (
      <NotificationBanner
        error
        title="There are some missing form fields or validation issues in this section."
        content="You will not be able to able to submit the form unless these errors are fixed. Please review this section again."
      />
    );
  }

  function renderSalesRepError() {
    return (
      <NotificationBanner
        error
        title="There are no Motion team members assigned to the applicant."
        content="You will not be able to able to submit the form unless this error is fixed. Please update the missing Motion team member information in the applicant's client profile."
      />
    );
  }
  function renderSectionWarning(sec: Array<string>) {
    if (sec?.length <= 0) return null;
    return (
      <NotificationBanner
        warning
        title="This section contains inconsistencies:"
        bullets={sec}
      />
    );
  }

  return (
    <>
      <div className={styles.margin}>
        <Heading size="h3" className={styles.grey500}>
          Application
        </Heading>
        <Text className={classNames(styles.grey500, styles.verticalMargin)}>
          Below are the sections of the application form that you are submitting
          for review. When you submit, edits will be locked until a Motion team
          member reviews the form. Once the form is reviewed, you will receive a
          notification.
        </Text>
      </div>
      {!salesRepStatus && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text>Motion Team Member Required</Text>
            <TextLink
              to={`/client/${clientFile.id}/edit`}
              disabled={readOnly}
            >
              Assign Motion team member(s)
            </TextLink>
          </div>
          {renderSalesRepError()}
          <br />
          <div className={styles.horizontalLine} />
        </div>
      )}
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <Text> Section 1 - Applicant&apos;s Biographical Information</Text>
          <TextLink
            onClick={() => {
              setSelectedSection("section1");
              window.scrollTo(0, 0);
            }}
          >
            {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
          </TextLink>
        </div>
        {renderSectionError(!sectionsValid.section1)}
        <br />
        {renderSectionWarning(sec1)}
        <div className={styles.horizontalLine} />
      </div>

      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <Text> Section 2 - Devices &amp; Eligibility</Text>
          <TextLink
            onClick={() => {
              setSelectedSection("section2");
              window.scrollTo(0, 0);
            }}
          >
            {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
          </TextLink>
        </div>
        {renderSectionError(!sectionsValid.section2)}
        <br />
        {renderSectionWarning(sec2)}
        <div className={styles.horizontalLine} />
      </div>

      {showSection2a && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text> Section 2a - Ambulation Aids</Text>
            <TextLink
              onClick={() => {
                setSelectedSection("section2a");
                window.scrollTo(0, 0);
              }}
            >
              {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
            </TextLink>
          </div>
          {renderSectionError(!sectionsValid.section2a)}
          <br />
          {renderSectionWarning(sec2a)}
          <div className={styles.horizontalLine} />
        </div>
      )}

      {showSection2b && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text> Section 2b - Manual Wheelchairs</Text>
            <TextLink
              onClick={() => {
                setSelectedSection("section2b");
                window.scrollTo(0, 0);
              }}
            >
              {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
            </TextLink>
          </div>
          {renderSectionError(!sectionsValid.section2b)}
          <br />
          {renderSectionWarning(sec2b)}
          <div className={styles.horizontalLine} />
        </div>
      )}

      {showSection2c && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text> Section 2c - Power Bases &amp; Power Scooters</Text>
            <TextLink
              onClick={() => {
                setSelectedSection("section2c");
                window.scrollTo(0, 0);
              }}
            >
              {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
            </TextLink>
          </div>
          {renderSectionError(!sectionsValid.section2c)}
          <br />
          {renderSectionWarning(sec2c)}
          <div className={styles.horizontalLine} />
        </div>
      )}

      {showSection2d && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text> Section 2d - Positioning Devices </Text>
            <TextLink
              onClick={() => {
                setSelectedSection("section2d");
                window.scrollTo(0, 0);
              }}
            >
              {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
            </TextLink>
          </div>
          {renderSectionError(!sectionsValid.section2d)}
          <br />
          {renderSectionWarning(sec2d)}
          <div className={styles.horizontalLine} />
        </div>
      )}

      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <Text> Section 3 - Applicant&apos;s Consent &amp; Signatures</Text>
          <TextLink
            onClick={() => {
              setSelectedSection("section3");
              window.scrollTo(0, 0);
            }}
          >
            {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
          </TextLink>
        </div>
        {renderSectionError(!sectionsValid.section3)}
        <br />
        {renderSectionWarning(sec3)}
        <div className={styles.horizontalLine} />
      </div>

      {showSection4 && (
        <div className={styles.margin}>
          <div className={styles.sectionEditGrid}>
            <Text> Section 4 - Signatures</Text>
            <TextLink
              onClick={() => {
                setSelectedSection("section4");
                window.scrollTo(0, 0);
              }}
            >
              {isSalesRep || isAdmin || readOnly ? "Review" : "Edit"}
            </TextLink>
          </div>
          {renderSectionError(!sectionsValid.section4)}
          <br />
          {renderSectionWarning(sec4)}
          <div className={styles.horizontalLine} />
        </div>
      )}

      <div className={styles.thickHorizontalLine} />

      <div className={styles.margin}>
        <Heading size="h3" className={styles.grey500}>
          Attachments
        </Heading>
        <Text className={classNames(styles.grey500, styles.verticalMargin)}>
          Your uploaded supporting documents:
        </Text>
      </div>

      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Client Signature</Text>
            <Text>
              Scan of the client’s signature for consent and approval. Required
              to complete Section 3 of application.
            </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.client_signature?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.client_signature?.name && (
          <NotificationBanner
            warning
            title="Required Document"
            content="This document is required for the ADP Form to be submitted."
          />
        )}
        {clientFile?.client_signature?.name && (
          <TextLink
            to={`${clientFile?.client_signature?.url}`}
            externalURL
          >
            {clientFile?.client_signature?.name}
          </TextLink>
        )}
        <div className={styles.horizontalLine} />
      </div>

      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Justification for Funding Chart</Text>
            <Text>
              Please upload a Justification for Funding Chart - Dynamic
              Positioning Device (power tilt and/or recline and/or power
              elevating leg rests), if required.
            </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.justification?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.justification?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.justification?.name && (
          <TextLink
            to={`${clientFile?.justification?.url}`}
            externalURL
          >
            {clientFile?.justification?.name}
          </TextLink>
        )}
        <div className={styles.horizontalLine} />
      </div>

      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Letter of Rationale</Text>
            <Text>
              If the client has previously accessed the Central Equipment Pool
              (CEP), and is moving to a non-CEP device, please upload a letter
              from CEP acknowledging the plan for the previous device. Also
              please review your clinical rationale in Section 2, page 2 to
              ensure the functional mobility status clearly indicates the change
              in the client&apos;s functional mobility status that has resulted
              in no longer needing a CEP chair.
            </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.letter_of_rationale?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.letter_of_rationale?.name && isCEP && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.letter_of_rationale?.name && (
          <TextLink
            to={`${clientFile?.letter_of_rationale?.url}`}
            externalURL
          >
            {clientFile?.letter_of_rationale?.name}
          </TextLink>
        )}

        <div className={styles.horizontalLine} />
      </div>
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Vendor Quote</Text>
            <Text>Scan of Vendor Quote</Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.quote?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.quote?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.quote?.name && (
          <TextLink
            to={`${clientFile?.quote?.url}`}
            externalURL
          >
            {clientFile?.quote?.name}
          </TextLink>
        )}
        <div className={styles.horizontalLine} />
      </div>
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Confirmation of Benefits </Text>
            <Text>Scan of Confirmation of Benefits</Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.confirmation_of_benefits?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.confirmation_of_benefits?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.confirmation_of_benefits?.name && (
          <TextLink
            to={`${clientFile?.confirmation_of_benefits?.url}`}
            externalURL
          >
            {clientFile?.confirmation_of_benefits?.name}
          </TextLink>
        )}
        <div className={styles.horizontalLine} />
      </div>
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Wet Signature Document </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.wet_signature?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.wet_signature?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.wet_signature?.name && (
          <TextLink
            to={`${clientFile?.wet_signature?.url}`}
            externalURL
          >
            {clientFile?.wet_signature?.name}
          </TextLink>
        )}
        <div className={styles.horizontalLine} />
      </div>
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Other Supporting Documents</Text>
            <Text>
              If you have multiple files to upload, please combine them in a
              single document and upload.
            </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.other_supporting_documents?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.other_supporting_documents?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.other_supporting_documents?.name && (
          <TextLink
            to={`${clientFile?.other_supporting_documents?.url}`}
            externalURL
          >
            {clientFile?.other_supporting_documents?.name}
          </TextLink>
        )}
      </div>
      <div className={styles.margin}>
        <div className={styles.sectionEditGrid}>
          <div>
            <Text weight="bold"> Other Supporting Documents</Text>
            <Text>
              If you have multiple files to upload, please combine them in a
              single document and upload.
            </Text>
          </div>
          <TextLink
            disabled={readOnly}
            onClick={() => { setUploadModalOpen(true); }}
          >
            {clientFile?.other_supporting_documents_alt?.name ? "Edit file" : "Upload file"}
          </TextLink>
        </div>
        {!clientFile?.other_supporting_documents_alt?.name && (
          <NotificationBanner
            warning
            title="Optional Document"
            content="This optional document has not been uploaded"
          />
        )}
        {clientFile?.other_supporting_documents_alt?.name && (
          <TextLink
            to={`${clientFile?.other_supporting_documents_alt?.url}`}
            externalURL
          >
            {clientFile?.other_supporting_documents_alt?.name}
          </TextLink>
        )}
      </div>
      <Modal
        title="Supporting Documents"
        isOpen={isUploadModalOpen}
        onClose={() => { setUploadModalOpen(false); }}
      >
        <UploadDocumentsModal
          clientFileId={clientFile.id}
          setUploadModalOpen={setUploadModalOpen}
        />
      </Modal>
    </>
  );
};

export default Summary;
